import { ReactNode, useState } from "react";
import { AdminSidebar } from "./AdminSidebar";
import { Button } from "@jambushrushti/components/ui/button";
import { Menu, Bell, Home } from "lucide-react";
import { Link } from "react-router-dom";

interface AdminLayoutProps {
  children: ReactNode;
  title?: string;
}

export function AdminLayout({ children, title }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#FAF7F2] text-stone-900">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <AdminSidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-stone-950/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="animate-fade-in absolute left-0 top-0 h-full w-72">
            <AdminSidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex min-h-screen w-full flex-1 flex-col">
        {/* Top Header */}
        <header className="shadow-xs sticky top-0 z-40 border-b border-amber-900/10 bg-white/85 backdrop-blur-md">
          <div className="flex h-14 items-center justify-between px-4 sm:h-16 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl text-stone-700 hover:bg-amber-100/50 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              {title && (
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-amber-500" />
                  <h1 className="font-heading text-lg font-bold tracking-tight text-stone-900 sm:text-xl">
                    {title}
                  </h1>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Home Link */}
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-xl border-amber-900/10 text-stone-600 hover:bg-amber-50 hover:text-amber-700"
                asChild
              >
                <Link to="/" title="View Public Website">
                  <Home className="h-4 w-4" />
                </Link>
              </Button>

              {/* Notifications */}
              <Button
                variant="outline"
                size="icon"
                className="relative h-9 w-9 rounded-xl border-amber-900/10 text-stone-600 hover:bg-amber-50 hover:text-amber-700"
              >
                <Bell className="h-4 w-4" />
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto px-2.5 py-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
